import { Box } from '@xometry/xometry_loft';
import React, { ReactNode, Component, useContext } from 'react';
import * as Sentry from '@sentry/react';
import Auth from '@xometry/auth-context';
import Logger from '../../shared/Logger';

type FallbackRenderer = (error: Error) => React.ReactNode;

interface User {
  id?: string;
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  prevScope: string;
  eventId?: string;
}

interface ErrorBoundaryComponentProps {
  fallback?: ReactNode | FallbackRenderer;
  scope?: string;
  errorText?: string;
  className?: string;
  children?: ReactNode;
  user?: User | null;
}

/**
 * ErrorBoundary component sends enriched errors to RUM.
 */
class ErrorBoundaryComponent extends Component<
  ErrorBoundaryComponentProps,
  ErrorBoundaryState
> {
  static defaultProps = {
    scope: 'error-boundary',
    errorText: "We're sorry, an unexpected error occured!",
    className: '',
  };

  private sessionIdCache: string | null = null;

  constructor(props: any) {
    super(props);
    this.state = { hasError: false, prevScope: props.scope };
  }

  private getSessionId = (): string => {
    if (!this.sessionIdCache) {
      this.sessionIdCache =
        sessionStorage.getItem('sessionId') ||
        localStorage.getItem('sessionId') ||
        `session_${Date.now()}`;

      // Store the session ID for future use
      if (!sessionStorage.getItem('sessionId')) {
        sessionStorage.setItem('sessionId', this.sessionIdCache);
      }
    }
    return this.sessionIdCache;
  };

  private generateErrorId = (): string => {
    // Use crypto.randomUUID if available, fallback to timestamp + random
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return `checkout_err_${crypto.randomUUID()}`;
    }
    return `checkout_err_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 11)}`;
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorId = this.generateErrorId();
    const sessionId = this.getSessionId();

    // Create rich error context for logging
    const errorContext = {
      errorId,
      boundaryLevel: 'component',
      sessionId,
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    // Non-blocking async error logging
    Promise.allSettled([
      // Sentry logging with context
      new Promise<void>((resolve) => {
        Sentry.withScope((scope) => {
          scope.setTag('errorBoundary.level', 'component');
          scope.setTag('errorBoundary.name', 'checkout-app');
          scope.setContext('applicationError', errorContext);

          if (this.props.user) {
            scope.setUser({
              id: this.props.user.id,
              email: this.props.user.emailAddress,
            });
          }

          scope.setLevel('error');
          Sentry.captureException(error);
          resolve();
        });
      }),
      // Primary logger (includes Coralogix integration)
      new Promise<void>((resolve) => {
        Logger.error('Component Error Boundary triggered', error, errorContext);
        resolve();
      }),
    ]).catch((loggingError) => {
      // Fallback: don't let logging errors crash the app
      Logger.error('Error logging failed', loggingError);
    });

    this.setState({ hasError: true, error, eventId: errorId });
  }

  render() {
    const { hasError, error } = this.state;

    if (hasError || error) {
      const { fallback, errorText } = this.props;

      // If a custom fallback is provided, use it
      if (fallback) {
        if (typeof fallback === 'function' && error) {
          return fallback(error);
        }
        return fallback;
      }

      // Default fallback UI
      return (
        <Box width="500px" textAlign="center" margin="30px auto 0">
          <Box fontSize="16px">{errorText}</Box>
          <a href={window.location.href}>
            Please refresh the page to try again.
          </a>
          <i
            className="fa fa-refresh"
            style={{ display: 'block', margin: '20px 0', fontSize: '60px' }}
          />
          <div>If the problem persists, please contact support@xometry.com</div>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Main ErrorBoundary component that provides user context
interface ErrorBoundaryProps {
  fallback?: ReactNode | FallbackRenderer;
  scope?: string;
  errorText?: string;
  className?: string;
  children?: ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  const { user } = useContext(Auth);

  return (
    <ErrorBoundaryComponent {...props} user={user}>
      {props.children}
    </ErrorBoundaryComponent>
  );
};

export default ErrorBoundary;
